/**
 * Keep list thumbnails in sync after a detail photo save / delete.
 *
 * ListKeepAlive leaves the list mounted with the payload from the last fetch.
 * The editor only writes the detail Redux slice — without this overlay the
 * cards keep showing the previous crop until a full browser reload.
 *
 * `updatedAt` is also appended to the resolved URL (`v=`) so a rebake that
 * overwrites the same S3 key still busts the browser / CardMedia cache.
 */

import { useEffect, useState } from 'react';

const patches = new Map();
const listeners = new Set();

export function peekEntityImagePatch(entityId) {
  if (!entityId) {
    return null;
  }
  return patches.get(entityId) || null;
}

export function applyEntityImagePatchToEntity(entity) {
  if (!entity || typeof entity !== 'object' || !entity.id) {
    return entity;
  }
  const patch = patches.get(entity.id);
  if (!patch) {
    return entity;
  }
  return {
    ...entity,
    [patch.imageField]: patch.image ? [patch.image] : [],
    ...(patch.documentField ? { [patch.documentField]: [] } : {}),
  };
}

export function withEntityImageCacheBust(url, entityId) {
  if (!url || typeof url !== 'string') {
    return url;
  }
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  const patch = peekEntityImagePatch(entityId);
  if (!patch?.updatedAt) {
    return url;
  }
  const stamp = String(patch.updatedAt);
  if (url.includes(`v=${stamp}`)) {
    return url;
  }
  return `${url}${url.includes('?') ? '&' : '?'}v=${stamp}`;
}

/** Call after POST/DELETE *-image so keep-alive lists pick up the new crop. */
export function syncListEntityImage({ entityId, imageField, image = null, documentField }) {
  if (!entityId || !imageField) {
    return;
  }
  patches.set(entityId, {
    imageField,
    image,
    documentField: documentField || null,
    updatedAt: Date.now(),
  });
  listeners.forEach((listener) => listener());
}

export function subscribeEntityImageListSync(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-render the caller (virtual list / keep-alive) when a photo is saved. */
export function useEntityImageListSync() {
  const [revision, setRevision] = useState(0);
  useEffect(
    () => subscribeEntityImageListSync(() => setRevision((value) => value + 1)),
    [],
  );
  return revision;
}

export function resetEntityImagePatchesForTests() {
  patches.clear();
}
